import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface AgentData {
    AgentCode: string;
    State: string;
    Price: string;
    TimePeriodInMonths: string;
    isSelected: boolean;
}

const styles = {
    body: {
        margin: '0px',
        padding: '5px',
        fontFamily: 'Trebuchet MS, Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'white',
        position: 'absolute' as const,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        boxSizing: 'border-box' as const,
    },
    titleBar: {
        color: '#2859BF',
        backgroundColor: '#c7ddfd',
        padding: '5px',
        fontSize: '12pt',
        fontFamily: 'Arial',
        textAlign: 'center' as const,
        fontWeight: 'bold' as const,
        border: '1px solid #80A0FF',
        borderBottom: '0px',
    },
    scrollableDiv: {
        flex: 1,
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
        border: '1px solid #80A0FF',
        width: '100%',
        backgroundColor: 'white',
    },
    headerRow: {
        fontFamily: 'Arial',
        fontSize: '10pt',
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        height: '32px',
        textAlign: 'left' as const,
    },
    headerCell: {
        padding: '8px',
        borderRight: '1px solid #80A0FF',
        borderBottom: '1px solid #80A0FF',
        textAlign: 'left' as const,
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        fontWeight: 'normal' as const,
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
    },
    row: {
        fontFamily: 'Arial',
        fontSize: '11pt',
        backgroundColor: 'white',
        verticalAlign: 'middle' as const,
        minHeight: '20px',
        color: 'black',
    },
    altRow: {
        fontFamily: 'Trebuchet MS',
        fontSize: '11pt',
        backgroundColor: '#F4FAFF',
        verticalAlign: 'middle' as const,
        minHeight: '20px',
        color: 'black',
    },
    cell: {
        padding: '8px',
        fontSize: '10pt',
        borderRight: '1px solid #80A0FF',
        borderBottom: '1px solid #80A0FF',
        fontFamily: 'Arial',
        wordWrap: 'break-word' as const,
        whiteSpace: 'normal' as const
    },
    buttonContainer: {
        textAlign: 'center' as const,
        paddingTop: '10px',
        flexShrink: 0,
    },
    button: {
        backgroundColor: '#E5E5E5',
        fontFamily: 'Arial',
        fontSize: '10pt',
        color: 'black',
        border: '1px solid #707070',
        height: '25px',
        minWidth: '75px',
        cursor: 'pointer',
        whiteSpace: 'nowrap' as const,
        padding: '0 10px',
        borderRadius: '3px',
        margin: '0 5px'
    },
    input: {
        width: '85px',
        fontWeight: 'bold',
        fontFamily: 'Arial',
        fontSize: '10pt',
        padding: '2px',
        border: '1px solid #707070'
    }
};

const EL_SelectAgentCodes: React.FC = () => {
    const [agents, setAgents] = useState<AgentData[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const schoolCode = params.get('SchoolCode');
                const prevAgents = params.get('Agents')?.split(',') || [];
                const prevPrices = params.get('Price')?.split(',') || [];
                const prevTms = params.get('tm')?.split(',') || [];

                if (!schoolCode) {
                    setErrorMsg('School Code is required.');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${API_BASE_URL}api/sales-agents/?SchoolCode=${schoolCode}`);

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const dbAgents = await response.json();

                const mappedAgents = dbAgents.map((dbAgent: any) => {
                    const index = prevAgents.indexOf(dbAgent.AgentCode);
                    const isSelected = index !== -1;

                    return {
                        AgentCode: dbAgent.AgentCode,
                        State: dbAgent.State || '',
                        Price: isSelected && prevPrices[index] ? prevPrices[index] : '',
                        TimePeriodInMonths: isSelected && prevTms[index] ? prevTms[index] : '',
                        isSelected: isSelected
                    };
                });

                setAgents(mappedAgents);
            } catch (err) {
                console.error("Failed to fetch agents: ", err);
                setErrorMsg('Failed to load agents.');
            } finally {
                setLoading(false);
            }
        };

        fetchAgents();
    }, []);

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setAgents(prev => prev.map(a => ({ ...a, isSelected: checked })));
    };

    const handleCheckboxChange = (agentCode: string) => {
        setAgents(prev => prev.map(a =>
            a.AgentCode === agentCode ? { ...a, isSelected: !a.isSelected } : a
        ));
    };

    const handleInputChange = (agentCode: string, field: 'Price' | 'TimePeriodInMonths', value: string) => {
        setAgents(prev => prev.map(a =>
            a.AgentCode === agentCode ? { ...a, [field]: value } : a
        ));
    };

    const handleOk = () => {
        const selectedAgents = agents.filter(a => a.isSelected);

        if (selectedAgents.length === 0) {
            alert("Please select Agent(s).");
            return;
        }

        const agentCodes: string[] = [];
        const states: string[] = [];
        const prices: string[] = [];
        const tms: string[] = [];

        for (const agent of selectedAgents) {
            if (!agent.Price || agent.Price.trim() === '') {
                alert(`Please enter the price for Agent ${agent.AgentCode}.`);
                return;
            }
            if (!agent.TimePeriodInMonths || agent.TimePeriodInMonths.trim() === '') {
                alert(`Please enter the Time Period(frequency) for Agent ${agent.AgentCode}.`);
                return;
            }

            agentCodes.push(agent.AgentCode);
            states.push(agent.State);
            prices.push(agent.Price);
            tms.push(agent.TimePeriodInMonths);
        }

        const retValue = `${agentCodes.join(',')}|${states.join(',')}|${prices.join(',')}|${tms.join(',')}`;

        if (window.opener) {
            window.opener.PopupReturnValue = retValue;
        }

        sessionStorage.setItem('AgentPopupReturn', retValue);

        window.close();
    };

    const handleCancel = () => {
        window.close();
    };

    if (loading) return <div style={styles.body}><Loading /></div>;
    if (errorMsg) return <div style={{ ...styles.body, color: 'red' }}>{errorMsg}</div>;

    const allSelected = agents.length > 0 && agents.every(a => a.isSelected);

    return (
        <div style={styles.body}>
            <div style={styles.titleBar}>
                Assign Agent(s) and price for required package
            </div>

            <div style={styles.scrollableDiv}>
                <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: 'auto' }} />
                        <col style={{ width: 'auto' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '150px' }} />
                    </colgroup>
                    <thead>
                        <tr style={styles.headerRow}>
                            <th style={{ ...styles.headerCell, textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={allSelected}
                                    style={{ cursor: 'pointer' }}
                                />
                            </th>
                            <th style={styles.headerCell}>Agent Code</th>
                            <th style={styles.headerCell}>State</th>
                            <th style={styles.headerCell}>Price</th>
                            <th style={styles.headerCell}>Time Period(months)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agents.map((agent, index) => {
                            const rowStyle = index % 2 === 0 ? styles.altRow : styles.row;

                            return (
                                <tr key={agent.AgentCode} style={rowStyle}>
                                    <td style={{ ...styles.cell, textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={agent.isSelected}
                                            onChange={() => handleCheckboxChange(agent.AgentCode)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </td>

                                    <td style={styles.cell}>{agent.AgentCode}</td>

                                    <td style={styles.cell}>{agent.State}</td>

                                    <td style={styles.cell}>
                                        <input
                                            type="text"
                                            style={styles.input}
                                            maxLength={10}
                                            value={agent.Price}
                                            disabled={!agent.isSelected}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    agent.AgentCode,
                                                    'Price',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </td>

                                    <td style={styles.cell}>
                                        <input
                                            type="text"
                                            style={{ ...styles.input, width: '55px' }}
                                            maxLength={5}
                                            value={agent.TimePeriodInMonths}
                                            disabled={!agent.isSelected}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    agent.AgentCode,
                                                    'TimePeriodInMonths',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={styles.buttonContainer}>
                <button onClick={handleOk} style={styles.button}>OK</button>
                <button onClick={handleCancel} style={styles.button}>Cancel</button>
            </div>
        </div>
    );
};

export default EL_SelectAgentCodes;